/**
 * VectorToolCard — Card 4 in the masterplan's per-layer Tool Cards.
 *
 * Composition shell that wires the WP-2 `SceneSelection` contract, the
 * `MapLibreAdapter` style hooks (added in this WP), the `terra-draw`
 * plugin (consumed through `usePluginStore.setActivePlugin`), and the
 * scoped `vectorClient.exportSelection` mock into one Material 3 card.
 *
 * The card respects the gTwin read-only permission flag: when set, all
 * edit / draw / export controls render disabled with a `lock` icon and
 * the playbook tooltip "Read-only by project permission".
 *
 * Source-of-truth citations:
 *   - docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md §7.4, §8.4
 *   - geosoft-platform-design-system/docs/DESIGN_POLICY.md §5
 *   - geosoft-platform-design-system/docs/ICON_SYSTEM_PLAYBOOK.md
 *   - frontend/src/app/sceneSelection.ts (WP-2)
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import {
  vectorClient,
  type VectorExportFeature,
  type VectorExportFormat,
} from '@geosoft/viewer-sdk';
import {
  Banner,
  Button,
  Card,
  Chip,
  Icon,
  IconButton,
  Stack,
  Text,
  Tooltip,
  tokens,
} from '@/design';
import { dispatchToolCardRequest, selectByLayerId } from '@/app/sceneSelection';
import { useViewerStore } from '@/state/useViewerStore';
import { usePluginStore } from '@/state/usePluginStore';
import { VectorStylePanel } from './VectorStylePanel';
import { VectorAttributeTable, type VectorAttributeRow } from './VectorAttributeTable';
import {
  DEFAULT_VECTOR_FILTER,
  DEFAULT_VECTOR_STYLE,
  deriveVectorSourceInfo,
  featureMatchesFilter,
  isVectorToolCardLayer,
  vectorFilterToMaplibre,
  vectorStyleToPaintDelta,
  type VectorFilter,
  type VectorStyleState,
  type MaplibreVectorPaintDelta,
} from './vectorStyleModel';

// ---------------------------------------------------------------------------
// Style-hook seam (thin pass-through; mirrors WP-4 adapter setters)
// ---------------------------------------------------------------------------

/**
 * Minimal, structural shape the ToolCard expects from any engine
 * adapter. Mirrors the WP-4 thin pass-through setters added on
 * `MapLibreAdapter`. Keeping the shape structural (not nominal) means
 * future engines (Cesium, deck.gl) can implement the same hooks
 * without a cyclic dependency on the chrome.
 */
export interface VectorStyleHook {
  setVectorPaint?: (layerId: string, delta: MaplibreVectorPaintDelta) => void;
  setVectorFilter?: (layerId: string, filter: unknown) => void;
  setVectorLabels?: (
    layerId: string,
    options: { readonly enabled: boolean; readonly field: string | null },
  ) => void;
  getVectorFeatures?: (layerId: string) => ReadonlyArray<VectorExportFeature>;
}

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

export interface VectorToolCardProps {
  /** Vector layer selected for inspection. */
  readonly layer: Layer | null;
  /**
   * Read-only flag from the gTwin permission model. Default reads
   * `useViewerStore.readOnly`; provided explicitly for tests and for
   * embeds that need a per-card override (e.g. share-link read-only).
   */
  readonly readOnly?: boolean;
  /** Engine style-hook seam — optional so the card degrades to a UI-only mock. */
  readonly engine?: VectorStyleHook | null;
  /** Feature collection override (otherwise pulled from `engine.getVectorFeatures`). */
  readonly features?: ReadonlyArray<VectorExportFeature>;
  /** Currently selected feature id, used for visual sync + scene-tree dispatch. */
  readonly selectedFeatureId?: string | number | null;
  readonly 'data-testid'?: string;
}

export function VectorToolCard({
  layer,
  readOnly: readOnlyProp,
  engine,
  features: featuresProp,
  selectedFeatureId = null,
  'data-testid': testId = 'vector-tool-card',
}: VectorToolCardProps) {
  const storeReadOnly = useViewerStore((state) => state.readOnly);
  const setActivePlugin = usePluginStore((state) => state.setActivePlugin);
  const setSceneSelection = useViewerStore((state) => state.setSceneSelection);

  const readOnly = readOnlyProp ?? storeReadOnly;
  const sectionId = useId();

  const [style, setStyle] = useState<VectorStyleState>(DEFAULT_VECTOR_STYLE);
  const [filter, setFilter] = useState<VectorFilter>(DEFAULT_VECTOR_FILTER);
  const [attributeOpen, setAttributeOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<VectorExportFormat>('geojson');
  const [exportNotice, setExportNotice] = useState<string | null>(null);

  const layerIsVector = isVectorToolCardLayer(layer);

  // ----- Features -----
  const features = useMemo<ReadonlyArray<VectorExportFeature>>(() => {
    if (featuresProp) return featuresProp;
    if (!layer || !engine?.getVectorFeatures) return [];
    try {
      return engine.getVectorFeatures(layer.id) ?? [];
    } catch (err) {
      console.warn('[VectorToolCard] getVectorFeatures threw', err);
      return [];
    }
  }, [engine, featuresProp, layer]);

  const attributeKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const feature of features) {
      for (const key of Object.keys(feature.properties ?? {})) {
        keys.add(key);
      }
    }
    return Array.from(keys).sort();
  }, [features]);

  const filteredFeatures = useMemo<ReadonlyArray<VectorAttributeRow>>(() => {
    if (!filter.attribute) return features;
    return features.filter((feature) =>
      featureMatchesFilter(feature.properties ?? {}, filter),
    );
  }, [features, filter]);

  // ----- Style propagation -----
  useEffect(() => {
    if (!layer || !engine?.setVectorPaint) return;
    try {
      engine.setVectorPaint(layer.id, vectorStyleToPaintDelta(style));
    } catch (err) {
      console.warn('[VectorToolCard] setVectorPaint failed', err);
    }
  }, [engine, layer, style]);

  useEffect(() => {
    if (!layer || !engine?.setVectorLabels) return;
    try {
      engine.setVectorLabels(layer.id, {
        enabled: style.labelsEnabled,
        field: style.labelField,
      });
    } catch (err) {
      console.warn('[VectorToolCard] setVectorLabels failed', err);
    }
  }, [engine, layer, style.labelField, style.labelsEnabled]);

  useEffect(() => {
    if (!layer || !engine?.setVectorFilter) return;
    try {
      engine.setVectorFilter(layer.id, vectorFilterToMaplibre(filter));
    } catch (err) {
      console.warn('[VectorToolCard] setVectorFilter failed', err);
    }
  }, [engine, filter, layer]);

  // ----- Actions -----
  const handleSelectFeature = useCallback(
    (feature: VectorAttributeRow) => {
      if (!layer) return;
      const featureId = feature.id ?? feature._rowKey;
      const selection = selectByLayerId(layer.id, {
        layerKind: layer.kind,
        featureId,
        source: 'tool-card',
      });
      setSceneSelection(selection);
      dispatchToolCardRequest(selection);
    },
    [layer, setSceneSelection],
  );

  const handleSwitchToDraw = useCallback(() => {
    if (readOnly) return;
    setActivePlugin('annotation');
  }, [readOnly, setActivePlugin]);

  const handleSwitchToMeasure = useCallback(() => {
    setActivePlugin('measurement');
  }, [setActivePlugin]);

  const handleExport = useCallback(() => {
    if (!layer) return;
    const exportable = filteredFeatures.length > 0 ? filteredFeatures : features;
    if (exportable.length === 0) {
      setExportNotice('Nothing to export — clear the filter or select features.');
      return;
    }
    const result = vectorClient.exportSelection({
      layerId: layer.id,
      features: exportable,
      format: exportFormat,
    });
    setExportNotice(
      `Exported ${exportable.length} features to ${result.filename} (${result.format}).`,
    );
  }, [exportFormat, features, filteredFeatures, layer]);

  // ----- Render -----
  if (!layer) {
    return (
      <Card level={2} header="Vector tools" data-testid={`${testId}-empty`}>
        <Stack direction="row" align="center" gap={2}>
          <Icon name="info" size={20} decorative />
          <Text variant="body" as="p" style={{ margin: 0 }}>
            Select a vector layer to access symbology, filters, and the attribute table.
          </Text>
        </Stack>
      </Card>
    );
  }

  if (!layerIsVector) {
    return (
      <Card level={2} header="Vector tools" data-testid={`${testId}-disabled`}>
        <Banner
          severity="info"
          title="Not a vector layer"
          body={
            <>
              <code style={{ fontFamily: tokens.font.family.mono }}>{layer.kind}</code>
              {' '}layers use a different ToolCard. Switch to a vector or feature-table layer to use these controls.
            </>
          }
        />
      </Card>
    );
  }

  const sourceInfo = deriveVectorSourceInfo(layer);
  const headerLabel = `${layer.label ?? layer.id} · vector tools`;

  return (
    <Card level={2} header={headerLabel} data-testid={testId}>
      <Stack direction="column" gap={3}>
        {/* Layer summary */}
        <Stack direction="row" gap={1} style={{ flexWrap: 'wrap' }}>
          <Chip icon="polyline" label={layer.kind} variant="neutral" />
          <Chip icon="language" label={layer.crs} variant="neutral" />
          <Chip icon={sourceInfo.kind === 'pmtiles' ? 'public' : 'info'} label={sourceInfo.kind} variant="neutral" />
          {readOnly ? (
            <Tooltip content="Read-only by project permission">
              <span>
                <Chip icon="lock" label="Read-only" variant="warning" />
              </span>
            </Tooltip>
          ) : null}
          {filter.attribute ? (
            <Chip icon="filter_list" label={`filter: ${filter.attribute}`} variant="neutral" />
          ) : null}
        </Stack>

        {/* Style */}
        <SectionBlock id={`${sectionId}-style`}>
          <VectorStylePanel
            state={style}
            availableLabelFields={attributeKeys}
            readOnly={readOnly}
            onChange={setStyle}
            data-testid={`${testId}-style`}
          />
        </SectionBlock>

        {/* Filter row (attribute equals / contains / isEmpty) */}
        <SectionBlock id={`${sectionId}-filter`}>
          <FilterRow
            availableAttributes={attributeKeys}
            value={filter}
            onChange={setFilter}
            readOnly={readOnly}
            testId={`${testId}-filter`}
          />
          <Text variant="caption" as="span" style={{ color: tokens.color.ink.secondary }}>
            Matching <strong>{filteredFeatures.length}</strong> of {features.length} features.
          </Text>
        </SectionBlock>

        {/* Select / query / attribute-table toggle */}
        <SectionBlock id={`${sectionId}-actions`}>
          <Stack direction="row" gap={2} style={{ flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              onClick={() => {
                const first = filteredFeatures[0];
                if (first) handleSelectFeature(first);
              }}
              data-testid={`${testId}-select-first`}
            >
              <Icon name="select_all" size={20} decorative />
              <span>Select first match</span>
            </Button>
            <Button
              variant="ghost"
              onClick={() => setAttributeOpen((prev) => !prev)}
              aria-expanded={attributeOpen}
              aria-controls={`${sectionId}-attributes`}
              data-testid={`${testId}-toggle-attributes`}
            >
              <Icon name="table" size={20} decorative />
              <span>{attributeOpen ? 'Hide attribute table' : 'Show attribute table'}</span>
            </Button>
          </Stack>
        </SectionBlock>

        {/* Attribute table — side panel within the Card region (NOT a stacked drawer) */}
        {attributeOpen ? (
          <SectionBlock id={`${sectionId}-attributes`}>
            <VectorAttributeTable
              features={filteredFeatures as ReadonlyArray<VectorAttributeRow>}
              columns={attributeKeys}
              idColumns={['id', 'ID', 'gid', 'osm_id', 'objectid']}
              selectedFeatureId={selectedFeatureId}
              state={features.length === 0 ? 'empty' : 'idle'}
              onSelect={handleSelectFeature}
              data-testid={`${testId}-attribute-table`}
            />
          </SectionBlock>
        ) : null}

        {/* Edit / draw — gated by read-only */}
        <SectionBlock id={`${sectionId}-edit`}>
          <Stack direction="column" gap={1}>
            <Text variant="caption" as="h4" style={{ margin: 0, color: tokens.color.ink.secondary }}>
              Edit and draw
            </Text>
            <Stack direction="row" gap={2} style={{ flexWrap: 'wrap' }}>
              <PermissionButton
                readOnly={readOnly}
                icon="edit"
                label="Edit features"
                onClick={handleSwitchToDraw}
                testId={`${testId}-edit`}
              />
              <PermissionButton
                readOnly={readOnly}
                icon="polyline"
                label="Draw new feature"
                onClick={handleSwitchToDraw}
                testId={`${testId}-draw`}
              />
              <Button
                variant="ghost"
                onClick={handleSwitchToMeasure}
                data-testid={`${testId}-measure`}
              >
                <Icon name="straighten" size={20} decorative />
                <span>Measure / buffer</span>
              </Button>
            </Stack>
            {readOnly ? (
              <Stack direction="row" align="center" gap={1}>
                <Icon name="lock" size={20} decorative />
                <Text variant="caption" as="span">
                  Read-only by project permission. Switch role or open the layer in
                  edit mode to unlock these controls.
                </Text>
              </Stack>
            ) : null}
          </Stack>
        </SectionBlock>

        {/* Export selection */}
        <SectionBlock id={`${sectionId}-export`}>
          <Stack direction="column" gap={1}>
            <Text variant="caption" as="h4" style={{ margin: 0, color: tokens.color.ink.secondary }}>
              Export selection
            </Text>
            <Stack direction="row" gap={2} align="center" style={{ flexWrap: 'wrap' }}>
              <label
                style={{
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.size.xs,
                  color: tokens.color.ink.secondary,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: tokens.space[1],
                }}
              >
                Format
                <select
                  aria-label="Export format"
                  value={exportFormat}
                  onChange={(event) => setExportFormat(event.target.value as VectorExportFormat)}
                  data-testid={`${testId}-export-format`}
                  style={selectStyle()}
                >
                  <option value="geojson">GeoJSON</option>
                  <option value="csv">CSV</option>
                  <option value="flatgeobuf">FlatGeobuf</option>
                </select>
              </label>
              <PermissionButton
                readOnly={false /* export is permitted in read-only */}
                icon="download"
                label="Export selection"
                onClick={handleExport}
                testId={`${testId}-export-action`}
              />
            </Stack>
            {exportNotice ? (
              <Banner
                severity="info"
                title="Export started"
                body={exportNotice}
                onDismiss={() => setExportNotice(null)}
                data-testid={`${testId}-export-notice`}
              />
            ) : null}
          </Stack>
        </SectionBlock>

        {/* OGC feature source info */}
        <SectionBlock id={`${sectionId}-source-info`}>
          <Stack direction="row" align="center" gap={2}>
            <Icon name="info" size={20} decorative />
            <Text variant="caption" as="h4" style={{ margin: 0, color: tokens.color.ink.secondary }}>
              Feature source info
            </Text>
          </Stack>
          <Stack direction="column" gap={1} style={{ marginTop: tokens.space[1] }}>
            <SourceRow label="Source kind" value={sourceInfo.kind} mono />
            <SourceRow label="Endpoint" value={sourceInfo.endpoint ?? 'Local / in-memory'} mono />
            <SourceRow
              label="Supported CRS"
              value={(sourceInfo.supportedCrs ?? [layer.crs]).join(', ')}
              mono
            />
            {sourceInfo.capabilities && sourceInfo.capabilities.length > 0 ? (
              <SourceRow
                label="Capabilities"
                value={sourceInfo.capabilities.join(', ')}
              />
            ) : null}
            <SourceRow label="Feature count" value={String(features.length)} mono />
          </Stack>
        </SectionBlock>
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface SectionBlockProps {
  readonly id: string;
  readonly children: ReactNode;
}

function SectionBlock({ id, children }: SectionBlockProps) {
  return (
    <section
      id={id}
      style={{
        background: tokens.color.surface[1],
        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        borderRadius: tokens.radius.md,
        padding: tokens.space[3],
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
      }}
    >
      {children}
    </section>
  );
}

interface FilterRowProps {
  readonly availableAttributes: ReadonlyArray<string>;
  readonly value: VectorFilter;
  readonly onChange: (next: VectorFilter) => void;
  readonly readOnly: boolean;
  readonly testId: string;
}

function FilterRow({ availableAttributes, value, onChange, readOnly, testId }: FilterRowProps) {
  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" align="center" gap={2}>
        <Icon name="filter_list" size={20} decorative />
        <Text variant="subtitle" as="h3" style={{ margin: 0 }}>
          Filter
        </Text>
      </Stack>
      <Stack direction="row" gap={2} style={{ flexWrap: 'wrap' }}>
        <select
          aria-label="Filter attribute"
          value={value.attribute ?? ''}
          disabled={readOnly}
          onChange={(event) =>
            onChange({
              ...value,
              attribute: event.target.value === '' ? null : event.target.value,
            })
          }
          data-testid={`${testId}-attribute`}
          style={selectStyle()}
        >
          <option value="">— Attribute —</option>
          {availableAttributes.map((attr) => (
            <option key={attr} value={attr}>
              {attr}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter operator"
          value={value.operator}
          disabled={readOnly || !value.attribute}
          onChange={(event) =>
            onChange({
              ...value,
              operator: event.target.value as VectorFilter['operator'],
            })
          }
          data-testid={`${testId}-operator`}
          style={selectStyle()}
        >
          <option value="equals">equals</option>
          <option value="contains">contains</option>
          <option value="isEmpty">is empty</option>
        </select>
        <input
          type="text"
          aria-label="Filter value"
          placeholder="value"
          value={value.value}
          disabled={readOnly || !value.attribute || value.operator === 'isEmpty'}
          onChange={(event) => onChange({ ...value, value: event.target.value })}
          data-testid={`${testId}-value`}
          style={inputStyle()}
        />
        <IconButton
          aria-label="Clear filter"
          onClick={() => onChange({ attribute: null, operator: 'equals', value: '' })}
          disabled={readOnly || !value.attribute}
          data-testid={`${testId}-clear`}
        >
          <Icon name="close" size={20} decorative />
        </IconButton>
      </Stack>
    </Stack>
  );
}

interface PermissionButtonProps {
  readonly readOnly: boolean;
  readonly icon: 'edit' | 'polyline' | 'download' | 'straighten';
  readonly label: string;
  readonly onClick: () => void;
  readonly testId: string;
}

function PermissionButton({ readOnly, icon, label, onClick, testId }: PermissionButtonProps) {
  const button = (
    <Button
      variant={readOnly ? 'ghost' : 'secondary'}
      disabled={readOnly}
      aria-disabled={readOnly}
      onClick={onClick}
      data-testid={testId}
      style={{
        opacity: readOnly ? 0.6 : 1,
        cursor: readOnly ? 'not-allowed' : 'pointer',
      }}
    >
      <Icon name={readOnly ? 'lock' : icon} size={20} decorative />
      <span>{label}</span>
    </Button>
  );
  if (!readOnly) return button;
  return (
    <Tooltip content="Read-only by project permission">
      <span>{button}</span>
    </Tooltip>
  );
}

interface SourceRowProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

function SourceRow({ label, value, mono }: SourceRowProps) {
  return (
    <Stack direction="row" gap={2} align="baseline">
      <Text
        variant="caption"
        as="span"
        style={{
          minWidth: tokens.layout.popoverSm,
          color: tokens.color.ink.secondary,
        }}
      >
        {label}
      </Text>
      <Text
        variant="body"
        as="span"
        style={{
          fontFamily: mono ? tokens.font.family.mono : tokens.font.family.ui,
          fontSize: tokens.font.size.sm,
        }}
      >
        {value}
      </Text>
    </Stack>
  );
}

function selectStyle() {
  return {
    minHeight: tokens.density.touchMin,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.input,
    paddingInline: tokens.space[2],
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}

function inputStyle() {
  return {
    minHeight: tokens.density.touchMin,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.input,
    paddingInline: tokens.space[2],
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}
